import * as Sentry from '@sentry/react'

/* ------------------------------------------------------------------
   گزارش خطای Frontend — سند مادر بند ۱۶ (Observability)
   ------------------------------------------------------------------
   🔴 بدون VITE_SENTRY_DSN هیچ چیزی راه‌اندازی نمی‌شود و هیچ درخواستی به
   شبکه نمی‌رود. پیش‌فرض عمدی است: توسعه محلی و تست E2E نباید بی‌سر و صدا
   به سرویس بیرونی وصل شوند.

   نمونه Sentry داخلی (self-hosted) است، پس DSN یک نشانی داخلی است.
   ------------------------------------------------------------------ */

/** آیا گزارش‌دهی پیکربندی شده است؟ */
export function isObservabilityEnabled(): boolean {
  return Boolean(import.meta.env.VITE_SENTRY_DSN)
}

export function initObservability(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN

  if (!dsn) return

  Sentry.init({
    dsn,

    // نسخه‌ای که خطا در آن رخ داده. همان مقداری است که هنگام Build به
    // Sentry اعلام می‌شود، وگرنه Stack Trace به Source Map وصل نمی‌شود و
    // فقط کد فشرده و بی‌معنی دیده می‌شود.
    release: import.meta.env.VITE_SENTRY_RELEASE,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,

    // ─────────────────────────────────────────────────────────────
    // 🔴 هرگز true نشود.
    //
    // sendDefaultPii یعنی مقدار ورودی‌های فرم، هدرها و IP کاربر ارسال
    // شود. این سامانه منابع انسانی است: فرم‌ها کد ملی، حقوق و شماره
    // موبایل دارند و فرم ورود رمز دارد. آن داده حق ندارد از مرز
    // سامانه خارج شود، حتی به سمت نمونه داخلی.
    // ─────────────────────────────────────────────────────────────
    sendDefaultPii: false,

    // Tracing فعلاً خاموش است؛ سیم‌کشی‌اش کامل است و روشن‌کردنش فقط
    // مقداردهی VITE_SENTRY_TRACES_SAMPLE_RATE هنگام Build است.
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0),

    integrations: [
      Sentry.browserTracingIntegration(),
    ],

    /* ------------------------------------------------------------
       نویزی که سیگنال را دفن می‌کند.

       ۴۰۱ یعنی Session تمام شده و ۴۰۳/۴۲۲ یعنی ورودی کاربر — هیچ‌کدام
       نقص سامانه نیستند و AuthProvider و فرم‌ها خودشان مدیریتشان
       می‌کنند. NETWORK_ERROR هم اغلب یعنی اینترنت کاربر قطع شده.
       ------------------------------------------------------------ */
    beforeSend(event, hint) {
      const error = hint?.originalException as
        | { name?: string; status?: number; code?: string; requestId?: string | null }
        | undefined

      if (error?.name === 'ApiError') {
        const status = error.status ?? 0

        if (status === 0 || (status >= 400 && status < 500)) return null

        // خطای ۵۰۰ از Backend می‌آید و همان‌جا هم گزارش شده. با چسباندن
        // request_id، رویداد Frontend و رویداد Backend به هم وصل می‌شوند.
        if (error.requestId) {
          event.tags = { ...event.tags, request_id: error.requestId }
        }

        if (error.code) {
          event.tags = { ...event.tags, api_error_code: error.code }
        }
      }

      return event
    },
  })
}

/* ------------------------------------------------------------------
   هویت کاربر روی رویدادها.

   🔴 فقط شناسه — نه نام، نه ایمیل، نه موبایل. برای پاسخ به «این خطا چند
   نفر را گرفته؟» کافی است و چیزی درباره «چه کسی» فاش نمی‌کند. عمداً با
   همان قاعده‌ای که در Backend اعمال شده هم‌راستاست.
   ------------------------------------------------------------------ */
export function setObservabilityUser(id: number | string | null): void {
  if (!isObservabilityEnabled()) return

  Sentry.setUser(id === null ? null : { id: String(id) })
}

/**
 * شناسه ردیابی هر درخواست به‌صورت Breadcrumb.
 *
 * وقتی خطایی گزارش می‌شود، این‌ها نشان می‌دهند درست پیش از خطا چه
 * درخواست‌هایی رفته و هرکدام چه `correlation_id` داشته‌اند — همان مقداری
 * که در لاگ Backend و در Jobهای صف هم هست.
 */
export function addRequestBreadcrumb(
  method: string | undefined,
  url: string | undefined,
  correlationId: string,
): void {
  if (!isObservabilityEnabled()) return

  Sentry.addBreadcrumb({
    category: 'api',
    type: 'http',
    level: 'info',
    message: `${(method ?? 'get').toUpperCase()} ${url ?? ''}`.trim(),
    data: { correlation_id: correlationId },
  })
}

/** خطای گرفته‌شده در Error Boundary — با نام ماژولی که شکسته است. */
export function reportBoundaryError(
  error: Error,
  componentStack: string | null | undefined,
  moduleName?: string,
): void {
  if (!isObservabilityEnabled()) return

  Sentry.withScope((scope) => {
    scope.setTag('error_boundary', moduleName ?? 'root')
    scope.setContext('react', { componentStack: componentStack ?? null })
    Sentry.captureException(error)
  })
}
